(() => {
  const PATCH_FLAG = '__lumiyearProductModalPinchZoom';

  function patchProductModal() {
    const ProductModal = customElements.get('product-modal');
    if (!ProductModal || ProductModal.prototype[PATCH_FLAG]) return;

    const proto = ProductModal.prototype;
    const originalHide = proto.hide;
    const originalShowActiveMedia = proto.showActiveMedia;

    proto[PATCH_FLAG] = true;

    proto.ensurePinchZoomState = function () {
      if (!this.pinchZoom) {
        this.pinchZoom = {
          pointers: new Map(),
          scale: 1,
          translateX: 0,
          translateY: 0,
          lastTapTime: 0,
        };
      }

      return this.pinchZoom;
    };

    proto.hide = function (...args) {
      this.resetPinchZoom?.();
      return originalHide.apply(this, args);
    };

    proto.showActiveMedia = function (...args) {
      const result = originalShowActiveMedia.apply(this, args);
      const activeMediaId = this.openedBy?.getAttribute('data-media-id');
      const activeMedia = activeMediaId ? this.querySelector(`img[data-media-id="${activeMediaId}"]`) : null;
      this.setupPinchZoom?.(activeMedia);
      return result;
    };

    proto.setupPinchZoom = function (activeMedia) {
      const image = activeMedia?.matches('img') ? activeMedia : null;
      const container = this.querySelector('[role="document"]');
      if (!image || !container || image.dataset.lumiyearPinchZoomReady === 'true') return;

      image.dataset.lumiyearPinchZoomReady = 'true';
      image.addEventListener('pointerdown', this.onPinchPointerDown.bind(this));
      image.addEventListener('pointermove', this.onPinchPointerMove.bind(this));
      image.addEventListener('pointerup', this.onPinchPointerEnd.bind(this));
      image.addEventListener('pointercancel', this.onPinchPointerEnd.bind(this));
      image.addEventListener('lostpointercapture', this.onPinchPointerEnd.bind(this));
      image.addEventListener('dblclick', (event) => {
        if (!this.isMobileModalZoom()) return;
        event.preventDefault();
        this.togglePinchZoom(event.clientX, event.clientY);
      });
    };

    proto.isMobileModalZoom = function () {
      return window.matchMedia('(max-width: 749px)').matches;
    };

    proto.getActiveZoomImage = function () {
      const activeMediaId = this.openedBy?.getAttribute('data-media-id');
      if (!activeMediaId) return null;
      return (
        this.querySelector(`img[data-media-id="${activeMediaId}"].active`) ||
        this.querySelector(`img[data-media-id="${activeMediaId}"]`)
      );
    };

    proto.onPinchPointerDown = function (event) {
      if (!this.isMobileModalZoom() || event.pointerType !== 'touch') return;

      const zoom = this.ensurePinchZoomState();
      const image = this.getActiveZoomImage();
      if (event.currentTarget !== image) return;

      image.setPointerCapture?.(event.pointerId);
      zoom.pointers.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
      });

      if (zoom.pointers.size === 1) {
        const now = Date.now();
        if (now - zoom.lastTapTime < 280) {
          event.preventDefault();
          this.togglePinchZoom(event.clientX, event.clientY);
          zoom.lastTapTime = 0;
        } else {
          zoom.lastTapTime = now;
        }

        zoom.dragStartX = event.clientX;
        zoom.dragStartY = event.clientY;
        zoom.startTranslateX = zoom.translateX;
        zoom.startTranslateY = zoom.translateY;
      }

      if (zoom.pointers.size === 2) {
        event.preventDefault();
        const points = Array.from(zoom.pointers.values());
        zoom.startDistance = this.getPinchDistance(points[0], points[1]);
        zoom.startMidpoint = this.getPinchMidpoint(points[0], points[1]);
        zoom.startScale = zoom.scale;
        zoom.startTranslateX = zoom.translateX;
        zoom.startTranslateY = zoom.translateY;
        image.classList.add('is-pinching');
      }
    };

    proto.onPinchPointerMove = function (event) {
      const zoom = this.ensurePinchZoomState();
      if (!this.isMobileModalZoom() || event.pointerType !== 'touch' || !zoom.pointers.has(event.pointerId)) return;

      const image = this.getActiveZoomImage();
      if (!image) return;

      zoom.pointers.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
      });

      if (zoom.pointers.size === 2) {
        event.preventDefault();
        const points = Array.from(zoom.pointers.values());
        const currentDistance = this.getPinchDistance(points[0], points[1]);
        const currentMidpoint = this.getPinchMidpoint(points[0], points[1]);

        zoom.scale = this.clamp(zoom.startScale * (currentDistance / zoom.startDistance), 1, 4);
        zoom.translateX = zoom.startTranslateX + (currentMidpoint.clientX - zoom.startMidpoint.clientX);
        zoom.translateY = zoom.startTranslateY + (currentMidpoint.clientY - zoom.startMidpoint.clientY);
        this.applyPinchZoom(image);
        return;
      }

      if (zoom.pointers.size === 1 && zoom.scale > 1) {
        event.preventDefault();
        zoom.translateX = zoom.startTranslateX + event.clientX - zoom.dragStartX;
        zoom.translateY = zoom.startTranslateY + event.clientY - zoom.dragStartY;
        this.applyPinchZoom(image);
      }
    };

    proto.onPinchPointerEnd = function (event) {
      const zoom = this.ensurePinchZoomState();
      if (!zoom.pointers.has(event.pointerId)) return;

      const image = this.getActiveZoomImage();
      zoom.pointers.delete(event.pointerId);
      image?.classList.remove('is-pinching');

      if (zoom.scale <= 1.02) {
        this.resetPinchZoom();
        return;
      }

      if (zoom.pointers.size === 1) {
        const point = Array.from(zoom.pointers.values())[0];
        zoom.dragStartX = point.clientX;
        zoom.dragStartY = point.clientY;
        zoom.startTranslateX = zoom.translateX;
        zoom.startTranslateY = zoom.translateY;
      }
    };

    proto.togglePinchZoom = function (clientX, clientY) {
      const zoom = this.ensurePinchZoomState();
      const image = this.getActiveZoomImage();
      if (!image) return;

      if (zoom.scale > 1) {
        this.resetPinchZoom();
        return;
      }

      const rect = image.getBoundingClientRect();
      zoom.scale = 2;
      zoom.translateX = (rect.left + rect.width / 2 - clientX) * 0.5;
      zoom.translateY = (rect.top + rect.height / 2 - clientY) * 0.5;
      this.applyPinchZoom(image);
    };

    proto.applyPinchZoom = function (image) {
      const zoom = this.ensurePinchZoomState();
      if (!image) return;

      if (zoom.scale <= 1.02) {
        if (zoom.pointers.size >= 2) {
          zoom.scale = 1;
          zoom.translateX = 0;
          zoom.translateY = 0;
          image.style.setProperty('--lumiyear-modal-zoom-scale', 1);
          image.style.setProperty('--lumiyear-modal-zoom-x', '0px');
          image.style.setProperty('--lumiyear-modal-zoom-y', '0px');
          image.classList.remove('is-zoomed');
          image.classList.add('is-pinching');
          return;
        }

        this.resetPinchZoom();
        return;
      }

      const container = this.querySelector('[role="document"]');
      const containerRect = container?.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const baseWidth = imageRect.width / zoom.scale;
      const baseHeight = imageRect.height / zoom.scale;
      const maxTranslateX = Math.max(0, (baseWidth * zoom.scale - (containerRect?.width || window.innerWidth)) / 2);
      const maxTranslateY = Math.max(0, (baseHeight * zoom.scale - (containerRect?.height || window.innerHeight)) / 2);

      zoom.translateX = this.clamp(zoom.translateX, -maxTranslateX, maxTranslateX);
      zoom.translateY = this.clamp(zoom.translateY, -maxTranslateY, maxTranslateY);
      image.style.setProperty('--lumiyear-modal-zoom-scale', zoom.scale);
      image.style.setProperty('--lumiyear-modal-zoom-x', `${zoom.translateX}px`);
      image.style.setProperty('--lumiyear-modal-zoom-y', `${zoom.translateY}px`);
      image.classList.add('is-zoomed');
    };

    proto.resetPinchZoom = function () {
      const zoom = this.ensurePinchZoomState();
      const image = this.getActiveZoomImage();
      zoom.pointers.clear();
      zoom.scale = 1;
      zoom.translateX = 0;
      zoom.translateY = 0;

      if (!image) return;
      image.classList.remove('is-zoomed', 'is-pinching');
      image.style.removeProperty('--lumiyear-modal-zoom-scale');
      image.style.removeProperty('--lumiyear-modal-zoom-x');
      image.style.removeProperty('--lumiyear-modal-zoom-y');
    };

    proto.getPinchDistance = function (firstPoint, secondPoint) {
      return Math.hypot(firstPoint.clientX - secondPoint.clientX, firstPoint.clientY - secondPoint.clientY);
    };

    proto.getPinchMidpoint = function (firstPoint, secondPoint) {
      return {
        clientX: (firstPoint.clientX + secondPoint.clientX) / 2,
        clientY: (firstPoint.clientY + secondPoint.clientY) / 2,
      };
    };

    proto.clamp = function (value, min, max) {
      return Math.min(max, Math.max(min, value));
    };
  }

  patchProductModal();
  customElements.whenDefined('product-modal').then(patchProductModal);
})();
